import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  TextField,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Upload as CloudUpload,
  CheckCircle,
  AlertCircle as ErrorIcon,
  AlertTriangle as Warning,
  Info,
  FileText as FilePresent,
  RefreshCw as Refresh,
  Download as FileDown
} from 'lucide-react';
import { importService, AttendanceFileInfo, AttendanceImportOptions, AttendanceImportResult, deviceService, Device } from '../services';

const AttendanceImport: React.FC = () => {
  const { t } = useTranslation();
  const steps = [t('attendanceImport.stepUpload'), t('attendanceImport.stepPreview'), t('attendanceImport.stepImport')];
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<AttendanceFileInfo | null>(null);
  const [preview, setPreview] = useState<Record<string, any>[]>([]);
  const [importResult, setImportResult] = useState<AttendanceImportResult | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import options
  const [options, setOptions] = useState<AttendanceImportOptions>({
    hasHeader: true,
    skipDuplicates: true,
    createMissingEmployees: false,
  });

  // Load devices on mount
  React.useEffect(() => {
    const loadDevices = async () => {
      try {
        const response = await deviceService.getAll();
        if (response.success) {
          setDevices(response.data);
        }
      } catch (err) {
        console.error('Device list load error:', err);
      }
    };
    loadDevices();
  }, []);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setLoading(true);

    try {
      const response = await importService.previewAttendance(selectedFile, 15);
      if (response.success && response.data) {
        setFileInfo(response.data.fileInfo);
        setPreview(response.data.preview);

        // Apply detected settings to options
        if (response.data.fileInfo) {
          const newOptions = { ...options };
          newOptions.hasHeader = response.data.fileInfo.hasHeader;
          newOptions.separator = response.data.fileInfo.separator;
          newOptions.dateFormat = response.data.fileInfo.dateFormat;

          // Apply column suggestions
          response.data.fileInfo.columnSuggestions?.forEach(suggestion => {
            if (suggestion.confidence >= 0.7) {
              switch (suggestion.suggestedMapping) {
                case 'EmployeeCode':
                  newOptions.employeeCodeColumn = suggestion.columnIndex.toString();
                  break;
                case 'EmployeeName':
                  newOptions.employeeNameColumn = suggestion.columnIndex.toString();
                  break;
                case 'DateTime':
                  newOptions.dateTimeColumn = suggestion.columnIndex.toString();
                  break;
                case 'Date':
                  newOptions.dateColumn = suggestion.columnIndex.toString();
                  break;
                case 'Time':
                  newOptions.timeColumn = suggestion.columnIndex.toString();
                  break;
                case 'CheckType':
                  newOptions.checkTypeColumn = suggestion.columnIndex.toString();
                  break;
              }
            }
          });

          setOptions(newOptions);
        }

        setActiveStep(1);
      } else {
        setError(t('attendanceImport.previewFailed'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('attendanceImport.fileUploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const response = await importService.importAttendance(file, options);
      if (response.success) {
        setImportResult(response.data);
        setActiveStep(2);
      } else {
        setError(response.message || t('attendanceImport.importFailed'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('attendanceImport.importError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setFileInfo(null);
    setPreview([]);
    setImportResult(null);
    setError(null);
    setOptions({
      hasHeader: true,
      skipDuplicates: true,
      createMissingEmployees: false,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFormatName = (format: number) => {
    const formats: Record<number, string> = {
      0: 'Otomatik',
      1: 'Excel',
      2: 'CSV',
      3: 'TXT',
      4: 'ZKTeco',
      5: 'Bilteks',
      6: 'Genel'
    };
    return formats[format] || 'Bilinmiyor';
  };

  const renderUploadStep = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <input
        type="file"
        accept=".xlsx,.xls,.csv,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        ref={fileInputRef}
      />
      <Paper
        sx={{
          p: 6,
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload size={64} className="text-blue-600 mb-4" />
        <Typography variant="h6" gutterBottom>
          {t('attendanceImport.uploadTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('attendanceImport.selectFileType')}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
          {t('attendanceImport.dragOrClick')}
        </Typography>
      </Paper>

      {/* 2026-08-14 (kullanici talebi): Ornek sablon indirme.
          Kullanici hangi sutunlarin bekledigini tahmin etmek zorunda kalmasin —
          dogru basliklara sahip bos Excel dosyasini indirip doldursun. */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<FileDown size={18} />}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = '/api/import/attendance/template';
          }}
        >
          Örnek Şablonu İndir (.xlsx)
        </Button>
      </Box>

      <Box sx={{ mt: 4, textAlign: 'left' }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('attendanceImport.supportedFormats')}:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label="Excel (.xlsx, .xls)" size="small" />
          <Chip label={t('attendanceImport.formatCSV')} size="small" />
          <Chip label={t('attendanceImport.formatTXT')} size="small" />
          <Chip label={t('attendanceImport.formatZKTeco')} size="small" />
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {t('attendanceImport.recommendedColumns')}
            <br />
            • <strong>{t('attendanceImport.colEmployeeCode')}</strong> - {t('attendanceImport.colEmployeeCodeDesc')}
            <br />
            • <strong>{t('attendanceImport.colDateTime')}</strong> - {t('attendanceImport.colDateTimeDesc')}
            <br />
            • <strong>{t('attendanceImport.colType')}</strong> ({t('attendanceImport.optional')}) - {t('attendanceImport.colTypeDesc')}
          </Typography>
        </Alert>
      </Box>
    </Box>
  );

  const renderPreviewStep = () => (
    <Box>
      {/* File Info */}
      {fileInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FilePresent size={24} className="text-blue-600" />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {file?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Format: {getFormatName(fileInfo.detectedFormat)} |
                  {t('attendanceImport.totalRows')}: {fileInfo.totalRows} |
                  {t('attendanceImport.headerRow')}: {fileInfo.hasHeader ? t('common.yes') : t('common.no')}
                </Typography>
              </Box>
            </Box>

            {fileInfo.columnSuggestions && fileInfo.columnSuggestions.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('attendanceImport.detectedColumns')}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {fileInfo.columnSuggestions.map((suggestion, idx) => (
                    <Chip
                      key={idx}
                      label={`${suggestion.columnName} → ${suggestion.suggestedMapping}`}
                      size="small"
                      color={suggestion.confidence >= 0.8 ? 'success' : 'default'}
                      variant={suggestion.confidence >= 0.7 ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Options */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('attendanceImport.importSettings')}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('attendanceImport.device')}</InputLabel>
              <Select
                value={options.deviceId || ''}
                label={t('attendanceImport.device')}
                onChange={(e) => setOptions({ ...options, deviceId: e.target.value ? Number(e.target.value) : undefined })}
              >
                <MenuItem value="">{t('attendanceImport.notSelected')}</MenuItem>
                {devices.map(device => (
                  <MenuItem key={device.id} value={device.id}>
                    {device.deviceName} - {device.location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.separator')}
              value={options.separator || ''}
              onChange={(e) => setOptions({ ...options, separator: e.target.value })}
              placeholder="; veya , veya tab"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.dateFormatLabel')}
              value={options.dateFormat || ''}
              onChange={(e) => setOptions({ ...options, dateFormat: e.target.value })}
              placeholder="dd.MM.yyyy HH:mm:ss"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.hasHeader}
                    onChange={(e) => setOptions({ ...options, hasHeader: e.target.checked })}
                  />
                }
                label={t('attendanceImport.hasHeader')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.skipDuplicates}
                    onChange={(e) => setOptions({ ...options, skipDuplicates: e.target.checked })}
                  />
                }
                label={t('attendanceImport.skipDuplicates')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.createMissingEmployees}
                    onChange={(e) => setOptions({ ...options, createMissingEmployees: e.target.checked })}
                  />
                }
                label={t('attendanceImport.createMissing')}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Column Mapping */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {t('attendanceImport.columnMappings')}:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.regNo')}
              value={options.employeeCodeColumn || ''}
              onChange={(e) => setOptions({ ...options, employeeCodeColumn: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.employeeName')}
              value={options.employeeNameColumn || ''}
              onChange={(e) => setOptions({ ...options, employeeNameColumn: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.dateTimeCol')}
              value={options.dateTimeColumn || ''}
              onChange={(e) => setOptions({ ...options, dateTimeColumn: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.dateCol')}
              value={options.dateColumn || ''}
              onChange={(e) => setOptions({ ...options, dateColumn: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.timeCol')}
              value={options.timeColumn || ''}
              onChange={(e) => setOptions({ ...options, timeColumn: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t('attendanceImport.checkInOut')}
              value={options.checkTypeColumn || ''}
              onChange={(e) => setOptions({ ...options, checkTypeColumn: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Preview Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('attendanceImport.dataPreview')}
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {fileInfo?.columns.map((col, idx) => (
                  <React.Fragment key={idx}>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      [{idx}] {col}
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.map((row, idx) => (
                <TableRow key={idx}>
                  {fileInfo?.columns.map((col, colIdx) => (
                    <React.Fragment key={colIdx}>
                      <TableCell>
                        {String(row[col] || '')}
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleReset}>
          {t('common.back')}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUpload size={20} />}
        >
          {loading ? t('attendanceImport.importing') : t('attendanceImport.stepImport')}
        </Button>
      </Box>
    </Box>
  );

  const renderResultStep = () => (
    <Box>
      {importResult && (
        <>
          {/* Summary */}
          <Alert
            severity={importResult.success ? 'success' : 'warning'}
            sx={{ mb: 3 }}
            icon={importResult.success ? <CheckCircle size={20} /> : <Warning size={20} />}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {importResult.success ? t('attendanceImport.importComplete') : t('attendanceImport.importCompleteWithErrors')}
            </Typography>
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {importResult.totalRows}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.totalRows')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {importResult.importedCount}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.imported')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">
                    {importResult.duplicateCount}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.duplicates')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {importResult.skippedCount}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.skipped')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {importResult.errorCount}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.errors')}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main">
                    {importResult.uniqueEmployeeCount}
                  </Typography>
                  <Typography variant="body2">{t('attendanceImport.employees')}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Date Range */}
          {importResult.minDate && importResult.maxDate && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('attendanceImport.dateRange')}: {new Date(importResult.minDate).toLocaleDateString()} - {new Date(importResult.maxDate).toLocaleDateString()}
            </Alert>
          )}

          {/* Errors */}
          {importResult.errors && importResult.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>{t('attendanceImport.errorsLabel')}:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {importResult.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
                {importResult.errors.length > 10 && (
                  <li>... {t('attendanceImport.andMoreErrors', { count: importResult.errors.length - 10 })}</li>
                )}
              </Box>
            </Alert>
          )}

          {/* Import Details */}
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('attendanceImport.importedRecords')}
              </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('attendanceImport.row')}</TableCell>
                    <TableCell>{t('attendanceImport.regNo')}</TableCell>
                    <TableCell>{t('attendanceImport.employee')}</TableCell>
                    <TableCell>{t('attendanceImport.dateTimeCol')}</TableCell>
                    <TableCell>{t('attendanceImport.typeCol')}</TableCell>
                    <TableCell>{t('attendanceImport.statusCol')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResult.importedRecords?.slice(0, 100).map((record, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{record.rowNumber}</TableCell>
                      <TableCell>{record.employeeCode}</TableCell>
                      <TableCell>{record.fullName}</TableCell>
                      <TableCell>
                        {record.logDateTime ? new Date(record.logDateTime).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell>{record.checkType || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          size="small"
                          color={
                            record.status === 'Imported' ? 'success' :
                            record.status === 'Duplicate' ? 'info' :
                            record.status === 'EmployeeNotFound' ? 'warning' :
                            record.status === 'Error' ? 'error' : 'default'
                          }
                        />
                        {record.errorMessage && (
                          <Typography variant="caption" display="block" color="error">
                            {record.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleReset}
              startIcon={<Refresh size={20} />}
            >
              {t('attendanceImport.newImport')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {t('attendanceImport.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('attendanceImport.subtitle')}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && activeStep === 0 && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('attendanceImport.analyzing')}
          </Typography>
        </Box>
      )}

      <Paper sx={{ p: 3 }}>
        {activeStep === 0 && renderUploadStep()}
        {activeStep === 1 && renderPreviewStep()}
        {activeStep === 2 && renderResultStep()}
      </Paper>
    </Box>
  );
};

export default AttendanceImport;
